import { Link, useSearchParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { ContentPiece, ManagedPublication, PieceStatus } from '../../shared/api/types';
import { EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { CHANNEL, KIND, STATUS } from './labels';

const TABS: PieceStatus[] = ['draft', 'in_review', 'approved', 'archived'];
/** Pestaña «Publicadas» (#68): lo que ya salió, con la fecha y dónde. Solo para quien publica. */
const PUBLISHED = 'publicadas';

/** Una fila por escrito publicado: sus canales vigentes (o programados) y la fecha más reciente. */
export function publishedPieces(items: ManagedPublication[]) {
  const byPiece = new Map<string, { pieceId: string; title: string; kind: ManagedPublication['kind']; at: string; where: ManagedPublication[] }>();
  for (const p of items) {
    if (p.state === 'withdrawn') continue;
    const row = byPiece.get(p.pieceId) ?? { pieceId: p.pieceId, title: p.title, kind: p.kind, at: p.publishAt, where: [] };
    row.where.push(p);
    if (p.publishAt > row.at) row.at = p.publishAt;
    byPiece.set(p.pieceId, row);
  }
  return [...byPiece.values()].sort((a, b) => b.at.localeCompare(a.at));
}

function PublishedList({ churchId }: { churchId: string }) {
  const list = useAsync((signal) => api.get<{ items: ManagedPublication[] }>(`/publishing/churches/${churchId}/publications`, signal).then((r) => publishedPieces(r.items)), [churchId]);
  if (list.status === 'loading') return <Loading />;
  if (list.status === 'error') return <ErrorState error={list.error} onRetry={list.reload} />;
  if (list.data.length === 0) return <EmptyState title="Aún no se ha publicado nada." />;
  const day = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <ul className="list card">
      {list.data.map((p) => (
        <li key={p.pieceId} className="list__item">
          <Link className="list__link" to={`/app/contenido/${p.pieceId}`}>
            <strong>{p.title}</strong>
            <span className="muted small">
              {KIND[p.kind]} · {p.where.map((w) => `${CHANNEL[w.channel]}${w.state === 'scheduled' ? ` (programada para el ${day(w.publishAt)})` : ` (${day(w.publishAt)})`}`).join(' · ')}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
const EMPTY: Record<PieceStatus, string> = {
  draft: 'No hay borradores.',
  in_review: 'Nada espera revisión.',
  approved: 'Aún no hay contenido aprobado.',
  archived: 'No hay contenido archivado.',
};

/** Contenido (Motor 12): artículos, predicaciones y estudios, con revisión antes de publicar. */
export function ContentPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [params, setParams] = useSearchParams();
  const canPublish = me.can('publishing.manage');
  const showPublished = canPublish && params.get('estado') === PUBLISHED;
  const tab = TABS.find((t) => t === params.get('estado')) ?? (me.can('content.approve') ? 'in_review' : 'draft');
  const list = useAsync(
    (signal) => (churchId && !showPublished ? api.get<{ items: ContentPiece[] }>(`/content/churches/${churchId}/pieces?status=${tab}`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId, tab, showPublished],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Contenido</h1>
        <p className="muted">Predicaciones, artículos y estudios. Se publican solo después de aprobarse.</p>
      </header>
      <div className="tabs" role="tablist" aria-label="Contenido">
        {TABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={!showPublished && tab === t} className={`tabs__tab${!showPublished && tab === t ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ estado: t })}>
            {STATUS[t].label}
          </button>
        ))}
        {canPublish ? (
          <button type="button" role="tab" aria-selected={showPublished} className={`tabs__tab${showPublished ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ estado: PUBLISHED })}>
            Publicadas
          </button>
        ) : null}
      </div>
      {me.can('content.write') ? (
        <div className="actions">
          <Link className="btn btn--primary" to="/app/contenido/nueva">
            <span>Nueva predicación o estudio</span>
          </Link>
        </div>
      ) : null}
      {showPublished ? <PublishedList churchId={churchId} /> : null}
      {!showPublished && list.status === 'loading' ? <Loading /> : null}
      {!showPublished && list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {!showPublished && list.status === 'ready' && list.data.length === 0 ? <EmptyState title={EMPTY[tab]} /> : null}
      {!showPublished && list.status === 'ready' && list.data.length > 0 ? (
        <ul className="list card">
          {list.data.map((p) => (
            <li key={p.id} className="list__item">
              <Link className="list__link" to={`/app/contenido/${p.id}`}>
                <strong>{p.title}</strong>
                <span className="muted small">
                  {KIND[p.kind]} · versión {p.currentRevision} · {p.createdBy.name} · {new Date(p.updatedAt).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                  {p.approvedRevision && p.status !== 'approved' ? ` · aprobada la versión ${p.approvedRevision}` : ''}
                </span>
                {p.summary ? <span className="small">{p.summary}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
